import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getBaseUrl } from './ApiContext';
import CompanyResearch from './CompanyResearch';
import CompanyTimeline from './CompanyTimeline';
import MonitoringStatus from './MonitoringStatus';
import './Company.css';

const baseUrl = getBaseUrl();

function hostOf(url) {
    try {
        return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`)
            .hostname.replace(/^www\./, '');
    } catch (e) {
        return url;
    }
}

const hrefOf = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

function Company() {
    const { id } = useParams();
    const [companyData, setCompanyData] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        setCompanyData(null);
        setTimeline(null);
        setError(null);
        axios.get(`${baseUrl}/api/companies/${id}/details`)
            .then(r => { if (live) setCompanyData(r.data); })
            .catch(err => {
                console.error('Error fetching company data:', err);
                if (live) setError('Could not load this company.');
            });
        // The timeline endpoint also carries the two sentences the page
        // opens with: what this company is to us, and what they asked for.
        axios.get(`${baseUrl}/api/companies/${id}/timeline`)
            .then(r => { if (live) setTimeline(r.data); })
            .catch(err => console.error('Error fetching company timeline:', err));
        return () => { live = false; };
    }, [id]);

    const reload = () => axios.get(`${baseUrl}/api/companies/${id}/timeline`)
        .then(r => setTimeline(r.data))
        .catch(err => console.error('Error refreshing timeline:', err));

    if (error) return <div className="co co-empty">{error}</div>;
    if (!companyData) return <div className="co co-empty">Loading…</div>;

    const name = companyData.name || companyData.companyName || companyData.displayName;
    const founders = companyData.founders || [];
    const summary = (timeline && timeline.summary) || {};

    const facts = [];
    if (companyData.website) {
        facts.push(
            <a key="site" href={hrefOf(companyData.website)} target="_blank" rel="noopener noreferrer">
                {hostOf(companyData.website)}
            </a>);
    }
    if (companyData.linkedin) {
        facts.push(
            <a key="li" href={hrefOf(companyData.linkedin)} target="_blank" rel="noopener noreferrer">
                LinkedIn
            </a>);
    }
    if (companyData.location) facts.push(<span key="loc">{companyData.location}</span>);
    if (!founders.length) facts.push(<span key="nf">no founders on record</span>);

    return (
        <div className="co">
            <h1 className="co-name">
                {name}
                {companyData.is_historical && (
                    <span className="historical-badge" title="Migrated from the prior Kyber Drive database">
                        Historical
                    </span>
                )}
            </h1>

            {facts.length > 0 && (
                <div className="co-facts">
                    {facts.reduce((out, item, i) => (
                        i ? [...out, <span key={`d${i}`} className="co-dot">·</span>, item] : [item]
                    ), [])}
                </div>
            )}

            {companyData.description && <p className="co-said">{companyData.description}</p>}

            {/* What they are to us, and what they wanted, in one sentence. */}
            {summary.relationship && (
                <p className="co-said">
                    {summary.relationship}
                    {summary.ask && ` They asked us for ${summary.ask.phrase}.`}
                </p>
            )}

            <MonitoringStatus
                companyId={id}
                status={companyData.monitoring_status}
                label={companyData.monitoring_status_label}
                lastChecked={companyData.last_monitored_at}
                onChanged={(change) => {
                    setCompanyData(prev => ({ ...prev, ...change }));
                    reload();
                }}
            />

            {founders.length > 0 && (
                <section className="co-section">
                    <h2>Founders</h2>
                    <ul className="co-people">
                        {founders.map(f => (
                            <li key={f.id}>
                                <span className="co-person-name">
                                    {[f.first_name, f.last_name].filter(Boolean).join(' ')}
                                </span>
                                {f.title && <span className="co-dim"> {f.title}</span>}
                                {f.email && <a className="co-person-link" href={`mailto:${f.email}`}>{f.email}</a>}
                                {f.linkedin && (
                                    <a className="co-person-link" href={hrefOf(f.linkedin)}
                                        target="_blank" rel="noopener noreferrer">LinkedIn</a>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <CompanyTimeline companyId={id} data={timeline} />
            <CompanyResearch companyId={id} />
        </div>
    );
}

export default Company;
